import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Slider,
  Typography,
  Select,
  MenuItem,
  Tooltip,
  Box,
  Button,
  Divider,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import FilterChip from 'src/App/Dashboards/Market/Components/Chips/FilterChip';
import { FilterVal } from 'src/interfaces/iproptypes';
import { setIndividualRevenuePotentialRange, updatePriceTiers } from 'src/store/Dashboard/actions';
import { PriceTiers } from 'src/interfaces/idashboard';
import {
  COLOR_PRIMARY,
  COLOR_RANGE_AIRBNB_MARKERS,
  DEFAULT_FILTER_VALUES,
  exchangeRates,
  PRICE_TIERS,
  rating_marks,
} from 'src/utils/const';
import styles from './styles';
import FilterActionButton from './FilterActionButton';
import { abbreviateNumber, convertCurrency } from 'src/utils/functions';
import { useCannotAccessFilter } from 'src/hooks/useFeatureAccess';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import { KPI_DEFINITIONS } from 'src/utils/kpiDefinitions';
import InfoTooltipContent from 'src/App/Dashboards/Market/Components/Tooltips/InfoTooltipContent';
import CloseIcon from '@mui/icons-material/Close';
import { COLOR_TEXT_GREY } from 'src/utils/colors';

interface PerformanceFilterProps {
  priceTiers: PriceTiers;
  setFilterVal: React.Dispatch<React.SetStateAction<FilterVal>>;
  filterVal: FilterVal;
  currency: string;
  isMobileView: boolean;
  setTotalFilterApplied: React.Dispatch<React.SetStateAction<number>>;
  handleCloseMenu: () => void;
  resetFilter: (filterNamesToReset: string[]) => void;
  revenuePotentialTiersDistribution: any;
  isUserAllowed: boolean;
  handleOnSampleClick: (plan: string) => void;
  UpgradeCTA: (closeMenu: () => void) => JSX.Element;
  FilterCoingSoonComponent: React.ReactNode | null;
}

const REVENUE_TIER_NAMES = ['Low revenue', 'Below avg', 'Above avg', 'Top earning'] as const;
const REVENUE_FILTER_VALUES = [0, 1, 2, 3] as const; // 4 tier indices → le25, le50, le75, le100

const getRevenueTierBoundaries = (data: any): number[] => {
  if (!data) return [];
  if (Array.isArray(data)) return [...data].sort((a, b) => a - b);
  return Object.keys(data)
    .map(Number)
    .filter(n => !Number.isNaN(n))
    .sort((a, b) => a - b);
};

const getRevenueTierRangeLabel = (
  tierIndex: number,
  boundaries: number[],
  currency: string
): string => {
  const abbrev = (n: number) => abbreviateNumber(convertCurrency(currency, n)).toLowerCase();
  // 4 tiers: boundaries = [20th, 40th, 60th, 80th] %ile from cache (aligned with buildMarketBitmaps rev_bucket)
  if (boundaries.length < 3) return '—';
  if (tierIndex === 0) return `≤ ${abbrev(boundaries[0])}`;
  if (tierIndex === 1) return `${abbrev(boundaries[0])}–${abbrev(boundaries[1])}`;
  if (tierIndex === 2) return `${abbrev(boundaries[1])}–${abbrev(boundaries[2])}`;
  // Tier 3 (Top earning) = backend le100 = revenue > 60th %ile (breaks[2])
  return `> ${abbrev(boundaries[2])}`;
};

// Persist across unmounts (e.g. when filter menu closes) so labels don't flash during reload
let lastValidRevenueBoundaries: number[] = [];

const PerformanceFilter: React.FC<PerformanceFilterProps> = ({
  priceTiers,
  setFilterVal,
  filterVal,
  currency,
  setTotalFilterApplied,
  handleCloseMenu,
  resetFilter,
  revenuePotentialTiersDistribution,
  isUserAllowed,
  handleOnSampleClick,
  UpgradeCTA,
  FilterCoingSoonComponent,
}) => {
  const dispatch = useDispatch();
  const [rating, setRating] = useState<number[]>(filterVal.rating || [3.9, 5]);
  const isAnyPriceTierChecked = filterVal.priceTierFilter.length > 0;

  const isAccessDenied = useCannotAccessFilter('performance');

  useEffect(() => {
    let appliedCount = 0;

    // Check priceTierFilter: active if any specific tier is toggled
    if (isAnyPriceTierChecked) {
      appliedCount++;
    }

    // Check hostType: active if not 'both'
    if (filterVal.hostType !== 'both') {
      appliedCount++;
    }

    // Check rating: active if not default [3.9, 5]
    // Ensure filterVal.rating exists and has 2 elements before comparing
    if (
      filterVal.rating &&
      filterVal.rating.length === 2 &&
      (filterVal.rating[0] !== DEFAULT_FILTER_VALUES.rating[0] ||
        filterVal.rating[1] !== DEFAULT_FILTER_VALUES.rating[1])
    ) {
      appliedCount++;
    }

    // Check number_of_reviews: active if not 'all'
    if (filterVal.number_of_reviews !== 'all') {
      appliedCount++;
    }

    // Check revenueFilter: active if not 'all'
    if (filterVal.revenueFilter.length > 0) {
      appliedCount++;
    }

    setTotalFilterApplied(appliedCount);
  }, [filterVal, priceTiers, setTotalFilterApplied]);

  const ratingOptions = Array.from({ length: 12 }, (_, i) => 3.9 + i * 0.1).map(
    v => Math.round(v * 10) / 10
  );

  const handlePriceTierFilter = (value: string) => {
    // const updatedPriceTiers = getUpdatedPriceTiers(priceTiers, value);
    // const newFilterValues = getUpdatedFilterValue(updatedPriceTiers);

    let newPriceTiers = [...filterVal.priceTierFilter];
    if (value === 'all') {
      newPriceTiers = [];
    } else {
      if ((newPriceTiers as string[]).includes(value)) {
        newPriceTiers = newPriceTiers.filter(tier => tier !== value);
      } else {
        (newPriceTiers as string[]).push(value);
      }
    }

    // dispatch(updatePriceTiers(newPriceTiers));
    setFilterVal(prev => ({ ...prev, priceTierFilter: newPriceTiers }));
  };

  const handleHostTypeChange = (value: 'independent' | 'professional' | 'both') => {
    setFilterVal(prev => ({ ...prev, hostType: value }));
  };

  const handleNumberOfReviewsChange = (value: string | 'all') => {
    setFilterVal(prev => ({ ...prev, number_of_reviews: value }));
  };

  const handleRatingChange = (type: 'min' | 'max', newValue: string | number) => {
    const value = Number(newValue);
    let currentRating = [...rating];

    if (type === 'min') {
      currentRating[0] = value;
      if (currentRating[0] > currentRating[1]) {
        currentRating[1] = Math.min(5, Math.max(value, currentRating[1]));
      }
    } else {
      // type === 'max'
      currentRating[1] = value;
      if (currentRating[1] < currentRating[0]) {
        currentRating[0] = Math.max(3.9, Math.min(value, currentRating[0]));
      }
    }
    // Ensure min is at least 3.9 and max is at most 5, and min <= max
    currentRating[0] = Math.max(3.9, currentRating[0]);
    currentRating[1] = Math.min(5, currentRating[1]);
    if (currentRating[0] > currentRating[1]) {
      if (type === 'min') currentRating[1] = currentRating[0];
      else currentRating[0] = currentRating[1];
    }

    setRating(currentRating);
    setFilterVal(prev => ({ ...prev, rating: currentRating }));
  };

  const handleRevenueFilter = (value: number | 'all') => {
    if (isAccessDenied) return;

    if (value === 'all') {
      return setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, revenueFilter: [] };
      });
    }

    if (isNaN(Number(value))) return null;

    let newValue: number[] = filterVal.revenueFilter as number[];

    if (newValue.includes(value)) {
      newValue.splice(newValue.indexOf(value), 1);
    } else {
      newValue.push(value);
    }

    // don't save this setting to localStorage because it can change when we select different mMrkets
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, revenueFilter: newValue };
    });
  };

  return (
    <Grid
      container
      direction="column"
      sx={{ maxWidth: 400, px: 2.5, py: 1, zIndex: 999999, position: 'relative' }}
    >
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="body1" style={{ fontWeight: 500 }}>
            Performance
          </Typography>

          <Button variant="text" size="small" onClick={handleCloseMenu}>
            <CloseIcon sx={{ color: COLOR_TEXT_GREY }} />
          </Button>
        </Grid>
        <Divider />
      </Grid>
      {!isUserAllowed && UpgradeCTA(handleCloseMenu)}
      {isUserAllowed && FilterCoingSoonComponent && FilterCoingSoonComponent}
      <Grid item>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography style={styles.filterTitleStyle} sx={{ mr: 1 }}>
            Price Tiers
          </Typography>
          <InfoButton tooltipContent={KPI_DEFINITIONS.ADR_listing_LTM.filterTierTooltip} />
        </Box>
        <Grid container spacing={1}>
          <Grid item>
            <FilterChip
              value="all"
              label={<CenteredLabel text="Any" />}
              handleOnClick={() => handlePriceTierFilter('all')}
              variant={filterVal.priceTierFilter.length === 0 ? 'filled' : 'outlined'}
              styles={chipStyles}
            />
          </Grid>
          {PRICE_TIERS.map((tier, index) => (
            <Grid item key={tier}>
              <FilterChip
                value={tier.toLowerCase()}
                label={
                  <PriceLabel
                    tier={tier}
                    index={index}
                    currency={currency}
                    priceTiers={priceTiers}
                  />
                }
                handleOnClick={() => handlePriceTierFilter(tier.toLowerCase())}
                variant={
                  (filterVal.priceTierFilter as string[]).includes(tier.toLowerCase())
                    ? 'filled'
                    : 'outlined'
                }
                styles={chipStyles}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography style={styles.filterTitleStyle} sx={{ mr: 1 }}>
            Revenue Filter
          </Typography>
          <InfoButton tooltipContent="Filter by estimated annual revenue potential. Revenue potential is calculated from a listing's recent performance and normalized to a full year. Tiers show how listings rank within this market (percentiles), not fixed cut-offs — so a few listings may appear slightly outside the displayed ranges." />
        </Box>
        <Grid container spacing={1}>
          <Grid item>
            <FilterChip
              value="all"
              label={<CenteredLabel text="Any" />}
              handleOnClick={() => handleRevenueFilter('all')}
              variant={filterVal.revenueFilter.length === 0 ? 'filled' : 'outlined'}
              styles={chipStyles}
            />
          </Grid>
          {REVENUE_FILTER_VALUES.map((revPotential, index) => {
            const boundaries = getRevenueTierBoundaries(revenuePotentialTiersDistribution);
            if (boundaries.length >= 3) lastValidRevenueBoundaries = boundaries;
            const displayBoundaries =
              boundaries.length >= 3 ? boundaries : lastValidRevenueBoundaries;
            const tierName = REVENUE_TIER_NAMES[index];
            const rangeLabel = getRevenueTierRangeLabel(index, displayBoundaries, currency);
            return (
              <Grid item key={`filter-chip-${revPotential}`}>
                <FilterChip
                  value={revPotential}
                  label={
                    <RevenueLabel tierName={tierName} rangeText={rangeLabel} />
                  }
                  handleOnClick={() => handleRevenueFilter(revPotential)}
                  variant={filterVal.revenueFilter.includes(revPotential) ? 'filled' : 'outlined'}
                  styles={chipStyles}
                />
              </Grid>
            );
          })}
        </Grid>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography style={styles.filterTitleStyle} sx={{ mr: 1 }}>
            Professionally Managed
          </Typography>
          <InfoButton tooltipContent="Select 'Yes' to see listings managed by hosts with 5 or more listings" />
        </Box>
        <FormControl fullWidth>
          <RadioGroup
            row
            value={filterVal.hostType}
            onChange={e =>
              handleHostTypeChange(e.target.value as 'independent' | 'professional' | 'both')
            }
          >
            <FormControlLabel
              value="professional"
              control={<Radio sx={{ color: COLOR_PRIMARY }} />}
              label="Yes"
            />
            <FormControlLabel
              value="independent"
              control={<Radio sx={{ color: COLOR_PRIMARY }} />}
              label="No"
            />
            <FormControlLabel
              value="both"
              control={<Radio sx={{ color: COLOR_PRIMARY }} />}
              label="Both"
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <Typography style={styles.filterTitleStyle}>Rating</Typography>
        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Grid item>
            <Select
              variant="outlined"
              size="small"
              value={rating[0]}
              onChange={e => handleRatingChange('min', e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
              sx={styles.rangeButtonStyle}
            >
              {ratingOptions.map(opt => (
                <MenuItem key={`min-rating-${opt}`} value={opt}>
                  {opt === 3.9 ? '<4.0' : opt.toFixed(1)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item>
            <Typography sx={{ mx: 1 }}>-</Typography>
          </Grid>
          <Grid item>
            <Select
              variant="outlined"
              size="small"
              value={rating[1]}
              onChange={e => handleRatingChange('max', e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
              sx={styles.rangeButtonStyle}
            >
              {ratingOptions.map(opt => (
                <MenuItem key={`max-rating-${opt}`} value={opt}>
                  {opt.toFixed(1)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <Typography style={styles.filterTitleStyle}>Number of Reviews</Typography>
        <FormControl fullWidth>
          <RadioGroup
            row
            value={filterVal.number_of_reviews}
            onChange={e =>
              handleNumberOfReviewsChange(e.target.value === 'all' ? 'all' : e.target.value)
            }
          >
            <FormControlLabel
              value="all"
              control={<Radio sx={{ color: COLOR_PRIMARY }} />}
              label="Any"
            />
            {[1, 3, 10, 30].map(val => (
              <FormControlLabel
                key={val}
                value={val + '+'}
                control={<Radio sx={{ color: COLOR_PRIMARY }} />}
                label={`${val}+`}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <FilterActionButton
          isUserAllowed={isUserAllowed}
          handleCloseMenu={handleCloseMenu}
          handleOnSampleClick={handleOnSampleClick}
          resetFilter={() =>
            resetFilter([
              'priceTierFilter',
              'hostType',
              'rating',
              'number_of_reviews',
              'revenueFilter',
            ])
          }
        />
      </Grid>
    </Grid>
  );
};

// --- Inline Components ---
const chipStyles = {
  width: '100px',
  height: '80px',
  ...styles.rangeButtonStyle,
};

const CenteredLabel = ({ text }: { text: string }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Typography variant="body1">{text}</Typography>
  </div>
);

const getPriceTierRangeLabel = (
  tier: string,
  priceTiers: PriceTiers,
  currency: string
): string => {
  const tierData = priceTiers[tier as keyof PriceTiers];
  if (!tierData?.range || tierData.range.length < 2) return '—';
  const symbol = exchangeRates[currency]?.symbol ?? '$';
  const abbrev = (n: number) =>
    abbreviateNumber(convertCurrency(currency, n)).toLowerCase();
  const [min, max] = tierData.range;
  if (min === 0) return `≤ ${symbol}${abbrev(max)}`;
  if (max >= 999999) return `≥ ${symbol}${abbrev(min)}`;
  return `${symbol}${abbrev(min)}–${symbol}${abbrev(max)}`;
};

const PriceLabel = ({
  tier,
  index,
  currency,
  priceTiers,
}: {
  tier: string;
  index: number;
  currency: string;
  priceTiers: PriceTiers;
}) => {
  const symbol = exchangeRates[currency]?.symbol || '$';
  const displaySymbol = symbol.length < 2 ? symbol : '$';
  const rangeLabel = getPriceTierRangeLabel(tier, priceTiers, currency);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="body1">
        {displaySymbol.repeat(index + 1)}
        <span style={{ color: '#ccc' }}>{displaySymbol.repeat(5 - (index + 1))}</span>
      </Typography>
      <Typography variant="body1">{tier}</Typography>
      <Typography
        variant="caption"
        style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: 2 }}
      >
        {rangeLabel}
      </Typography>
    </div>
  );
};

const RevenueLabel = ({ tierName, rangeText }: { tierName: string; rangeText: string }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Typography
      variant="body2"
      style={{
        fontSize: '0.75rem',
        color: '#999',
        letterSpacing: '0.08em',
      }}
    >
      REV
    </Typography>
    <Typography variant="body1">{tierName}</Typography>
    <Typography
      variant="caption"
      style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: 2 }}
    >
      {rangeText}
    </Typography>
  </div>
);

// --- Logic Helpers ---
const getUpdatedPriceTiers = (tiers: PriceTiers, value: string): PriceTiers => {
  if (value === 'all') {
    return Object.fromEntries(
      Object.entries(tiers).map(([tier, data]) => [tier, { ...data, toggled: false }])
    ) as PriceTiers;
  }

  return {
    ...tiers,
    [value]: { ...tiers[value], toggled: !tiers[value].toggled },
  };
};

const getUpdatedFilterValue = (tiers: PriceTiers): (number[] | 'all')[] => {
  const selected = Object.values(tiers)
    .filter(t => t.toggled)
    .map(t => t.range);

  return selected.length > 0 ? selected : ['all'];
};

export default PerformanceFilter;
